import { useMemo, useState } from 'react';
import { BookOpen, FileText, Link2, Search, Sparkles } from 'lucide-react';

import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { Button } from '@/components/ui/button';
import { BrainShell } from './BrainSegments';

type KnowledgeSource = {
  id: string;
  title: string;
  kind: 'Playbook' | 'Policy' | 'Schema' | 'FAQ';
  scope: string;
  status: 'Synced' | 'Draft' | 'Stale';
  updated: string;
  chunks: number;
};

const MOCK_SOURCES: KnowledgeSource[] = [
  {
    id: 'kb-cms',
    title: 'CMS publishing playbook',
    kind: 'Playbook',
    scope: 'Content · pages, fields, tags',
    status: 'Synced',
    updated: '2h ago',
    chunks: 48,
  },
  {
    id: 'kb-listings',
    title: 'Listings moderation SOP',
    kind: 'Playbook',
    scope: 'Listings · review queues',
    status: 'Synced',
    updated: 'Yesterday',
    chunks: 36,
  },
  {
    id: 'kb-payments',
    title: 'Payments & refunds glossary',
    kind: 'FAQ',
    scope: 'Payments · disputes',
    status: 'Synced',
    updated: '3d ago',
    chunks: 22,
  },
  {
    id: 'kb-privacy',
    title: 'Admin data access policy',
    kind: 'Policy',
    scope: 'Users · PII handling',
    status: 'Draft',
    updated: '1w ago',
    chunks: 14,
  },
  {
    id: 'kb-api',
    title: 'Admin API entity map',
    kind: 'Schema',
    scope: 'Platform · resources',
    status: 'Stale',
    updated: '2w ago',
    chunks: 91,
  },
];

const statusClass: Record<KnowledgeSource['status'], string> = {
  Synced: 'bg-emerald-50 text-emerald-800 border-emerald-100',
  Draft: 'bg-amber-50 text-amber-900 border-amber-100',
  Stale: 'bg-gray-100 text-gray-700 border-gray-200',
};

const BrainKnowledgePage = () => {
  useBreadcrumbs([
    { label: 'Brain', href: '/admin/ai' },
    { label: 'Knowledge base' },
  ]);

  const [query, setQuery] = useState('');

  const sources = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return MOCK_SOURCES;
    return MOCK_SOURCES.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.scope.toLowerCase().includes(q) ||
        s.kind.toLowerCase().includes(q),
    );
  }, [query]);

  return (
    <BrainShell
      title="Knowledge base"
      description="Mock sources Brain can cite when answering admin questions."
      actions={
        <>
          <Button variant="outline" size="sm" className="gap-1.5" disabled>
            <Link2 className="h-3.5 w-3.5" />
            Connect source
          </Button>
          <Button size="sm" className="gap-1.5" disabled>
            <Sparkles className="h-3.5 w-3.5" />
            Index now
          </Button>
        </>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[16rem] flex-1 max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search sources…"
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
          />
        </div>
        <p className="text-xs text-gray-500">
          {sources.length} source{sources.length === 1 ? '' : 's'} · mock data
        </p>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3 font-medium">Source</th>
              <th className="px-4 py-3 font-medium">Kind</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Chunks</th>
              <th className="px-4 py-3 font-medium">Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sources.map((s) => (
              <tr key={s.id} className="hover:bg-gray-50/80">
                <td className="px-4 py-3">
                  <div className="flex items-start gap-2.5">
                    <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
                      {s.kind === 'Schema' ? (
                        <FileText className="h-4 w-4" />
                      ) : (
                        <BookOpen className="h-4 w-4" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{s.title}</p>
                      <p className="text-xs text-gray-500">{s.scope}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-700">{s.kind}</td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-medium ${statusClass[s.status]}`}
                  >
                    {s.status}
                  </span>
                </td>
                <td className="px-4 py-3 tabular-nums text-gray-700">{s.chunks}</td>
                <td className="px-4 py-3 text-gray-500">{s.updated}</td>
              </tr>
            ))}
            {sources.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-500">
                  No sources match “{query}”.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </BrainShell>
  );
};

export default BrainKnowledgePage;
